import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { AnimatedProgress } from './AnimatedProgress';
import { Goal } from '@/hooks/use-goals';
import {
  PiggyBank,
  Shield,
  TrendingDown,
  MoreVertical,
  Plus,
  Minus,
  Edit,
  Trash2,
  Calendar,
  Target,
} from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface GoalCardProps {
  goal: Goal;
  onEdit: (goal: Goal) => void;
  onDelete: (id: string) => void;
  onDeposit: (id: string, amount: number, accountId: string) => void;
  onWithdraw: (id: string, amount: number, accountId: string) => void;
}

const TYPE_CONFIG = {
  simple_savings: {
    icon: PiggyBank,
    label: 'Poupança',
    color: 'bg-primary/10 text-primary',
  },
  emergency_fund: {
    icon: Shield,
    label: 'Emergência',
    color: 'bg-info/10 text-info',
  },
  category_limit: {
    icon: TrendingDown,
    label: 'Limite',
    color: 'bg-warning/10 text-warning',
  },
};

export function GoalCard({
  goal,
  onEdit,
  onDelete,
  onDeposit,
  onWithdraw,
}: GoalCardProps) {
  const [transactionModal, setTransactionModal] = useState<'deposit' | 'withdraw' | null>(null);
  const [amount, setAmount] = useState('');
  const [accounts, setAccounts] = useState<{ id: string; name: string; type: string }[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');

  useEffect(() => {
    supabase
      .from('accounts')
      .select('id, name, type')
      .eq('is_active', true)
      .eq('archived', false)
      .in('type', ['checking', 'savings'])
      .order('name')
      .then(({ data }) => setAccounts(data || []));
  }, []);

  useEffect(() => {
    if (!transactionModal) {
      setSelectedAccountId('');
    }
  }, [transactionModal]);

  const config = TYPE_CONFIG[goal.type];
  const Icon = config.icon;
  const percentage = goal.target_amount > 0
    ? (goal.current_amount / goal.target_amount) * 100
    : 0;
  const isComplete = percentage >= 100;

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);

  const getDaysUntilTarget = () => {
    if (!goal.target_date) return null;
    const days = differenceInDays(new Date(goal.target_date), new Date());
    return days;
  };

  const daysUntil = getDaysUntilTarget();

  const handleTransaction = () => {
    const value = parseFloat(amount);
    if (isNaN(value) || value <= 0 || !selectedAccountId) return;

    if (transactionModal === 'deposit') {
      onDeposit(goal.id, value, selectedAccountId);
    } else {
      onWithdraw(goal.id, value, selectedAccountId);
    }

    setAmount('');
    setTransactionModal(null);
  };

  return (
    <>
      <Card className={`relative overflow-hidden transition-all duration-300 hover:shadow-lg ${isComplete ? 'ring-2 ring-success' : ''}`}>
        {isComplete && (
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-success to-primary animate-pulse" />
        )}

        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className={`p-2 rounded-lg ${config.color}`}>
                <Icon className="h-4 w-4" />
              </div>
              <div>
                <CardTitle className="text-base">{goal.name}</CardTitle>
                <Badge variant="outline" className="mt-1 text-xs">
                  {config.label}
                </Badge>
              </div>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(goal)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Editar
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => onDelete(goal.id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Excluir
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Progress */}
          <div className="space-y-2">
            <AnimatedProgress
              value={goal.current_amount}
              max={goal.target_amount}
              size="lg"
              showLabel
            />
            <div className="flex justify-between text-sm">
              <span className="font-medium">{formatCurrency(goal.current_amount)}</span>
              <span className="text-muted-foreground">
                de {formatCurrency(goal.target_amount)}
              </span>
            </div>
          </div>

          {/* Info */}
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            {goal.target_date && (
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>
                  {format(new Date(goal.target_date), "dd 'de' MMM", { locale: ptBR })}
                </span>
                {daysUntil !== null && daysUntil > 0 && (
                  <span className="text-foreground font-medium">
                    ({daysUntil} dias)
                  </span>
                )}
              </div>
            )}
            {goal.auto_transfer && (
              <div className="flex items-center gap-1">
                <Target className="h-3 w-3" />
                <span>
                  Auto: {goal.auto_transfer_percentage
                    ? `${goal.auto_transfer_percentage}%`
                    : formatCurrency(goal.auto_transfer_amount || 0)}
                </span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => setTransactionModal('deposit')}
            >
              <Plus className="h-4 w-4 mr-1" />
              Depositar
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => setTransactionModal('withdraw')}
              disabled={goal.current_amount <= 0}
            >
              <Minus className="h-4 w-4 mr-1" />
              Sacar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Transaction Modal */}
      <Dialog
        open={transactionModal !== null}
        onOpenChange={() => setTransactionModal(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {transactionModal === 'deposit' ? 'Depositar em' : 'Sacar de'} {goal.name}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">Valor</label>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="0,00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-2"
            />
            {transactionModal === 'withdraw' && (
              <p className="text-xs text-muted-foreground mt-2">
                Saldo disponível: {formatCurrency(goal.current_amount)}
              </p>
            )}
            <div className="space-y-2 mt-4">
              <Label>
                {transactionModal === 'deposit' ? 'Conta de Origem' : 'Conta de Destino'}
              </Label>
              <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma conta..." />
                </SelectTrigger>
                <SelectContent>
                  {(['checking', 'savings'] as const).map(type => {
                    const group = accounts.filter(a => a.type === type);
                    if (group.length === 0) return null;
                    return (
                      <SelectGroup key={type}>
                        <SelectLabel>
                          {type === 'checking' ? 'Contas Corrente' : 'Cofrinhos'}
                        </SelectLabel>
                        {group.map(acc => (
                          <SelectItem key={acc.id} value={acc.id} className="pl-10">
                            {acc.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransactionModal(null)}>
              Cancelar
            </Button>
            <Button onClick={handleTransaction} disabled={!amount || parseFloat(amount) <= 0 || !selectedAccountId}>
              {transactionModal === 'deposit' ? 'Depositar' : 'Sacar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
